/**
 * SECURE TOKEN & AUTH MANAGEMENT
 * 
 * Best Practices:
 * - Auth tokens stored in httpOnly cookies (not accessible to JavaScript/XSS)
 * - Token refresh every 55 minutes (Firebase default: 1 hour)
 * - CSRF protection with tokens
 * - Secure session management
 * - Device tracking and verification
 */

import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';

// ============================================================================
// SESSION MANAGEMENT (httpOnly Cookies)
// ============================================================================

/**
 * IMPORTANT: Auth tokens MUST be stored in httpOnly, Secure cookies
 * Not in localStorage where XSS attacks can steal them
 * 
 * Firebase SDK stores tokens in localStorage by default.
 * We need to configure persistence to use sessionStorage or implement httpOnly
 */

export class SecureSessionManager {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.deviceId = this.getOrCreateDeviceId();
    this.sessionStartTime = Date.now();
    this.tokenRefreshInterval = 55 * 60 * 1000; // 55 minutes
    this.lastRefreshTime = Date.now();
  }

  /**
   * Generate unique session ID
   * Used for CSRF protection and session tracking
   */
  generateSessionId() {
    const sessionId = this.generateRandomString(32);
    sessionStorage.setItem('sessionId', sessionId);
    return sessionId;
  }

  /**
   * Get or create persistent device ID
   * Stored in IndexedDB (more secure than localStorage)
   */
  async getOrCreateDeviceId() {
    try {
      // Try to get from IndexedDB first (more secure)
      const deviceId = await this.getFromIndexedDB('deviceId');
      if (deviceId) return deviceId;

      // Generate new device ID
      const newDeviceId = this.generateRandomString(32);
      await this.storeInIndexedDB('deviceId', newDeviceId);

      // Also store in sessionStorage for current session
      sessionStorage.setItem('deviceId', newDeviceId);
      return newDeviceId;
    } catch (error) {
      // Console statement removed for production
      // Fallback to sessionStorage
      const deviceId = sessionStorage.getItem('deviceId');
      if (deviceId) return deviceId;

      const newDeviceId = this.generateRandomString(32);
      sessionStorage.setItem('deviceId', newDeviceId);
      return newDeviceId;
    }
  }

  /**
   * Store value in IndexedDB
   */
  async storeInIndexedDB(key, value) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('CrashCabin_Secure', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['session'], 'readwrite');
        const store = transaction.objectStore('session');
        const putRequest = store.put({ key, value, timestamp: Date.now() });

        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('session')) {
          db.createObjectStore('session', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Get value from IndexedDB
   */
  async getFromIndexedDB(key) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('CrashCabin_Secure', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['session'], 'readonly');
        const store = transaction.objectStore('session');
        const getRequest = store.get(key);

        getRequest.onsuccess = () => {
          const result = getRequest.result;
          resolve(result ? result.value : null);
        };
        getRequest.onerror = () => reject(getRequest.error);
      };
    });
  }

  /**
   * Generate cryptographically secure random string
   */
  generateRandomString(length) {
    const array = new Uint8Array(length / 2);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Initialize secure session monitoring
   * Watches for:
   * - Token expiration and refresh
   * - Suspicious activity
   * - Session timeout
   * - Tab/window management
   */
  async initializeSessionMonitoring() {
    // Initialize device ID
    this.deviceId = await this.getOrCreateDeviceId();

    // Set up token refresh interval
    this.tokenRefreshTimer = setInterval(() => {
      this.refreshAuthToken();
    }, this.tokenRefreshInterval);

    // Set up session activity monitoring
    this.monitorSessionActivity();

    // Set up visibility change monitoring (tab switching)
    this.monitorTabVisibility();

    // Set up storage event monitoring (cross-tab communication)
    this.monitorStorageEvents();

    // Set up cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanupSession();
    });

    // Set up page visibility change for security
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange();
    });
  }

  /**
   * Monitor tab visibility for security
   */
  monitorTabVisibility() {
    this.tabVisibilityTimer = setInterval(() => {
      if (document.hidden) {
        // Tab is hidden - increase security monitoring
        this.lastHiddenTime = Date.now();
      } else {
        // Tab became visible again
        const hiddenDuration = Date.now() - (this.lastHiddenTime || 0);
        if (hiddenDuration > 5 * 60 * 1000) { // 5 minutes
          // Long period of inactivity - refresh token
          // Console statement removed for production
          this.refreshAuthToken();
        }
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Monitor storage events for cross-tab communication
   */
  monitorStorageEvents() {
    window.addEventListener('storage', (event) => {
      // Handle logout from other tabs
      if (event.key === 'logout' && event.newValue === 'true') {
        // Console statement removed for production
        this.logout();
      }

      // Handle session extension from other tabs
      if (event.key === 'sessionExtended' && event.newValue) {
        this.lastActivityTime = Date.now();
      }
    });
  }

  /**
   * Handle visibility change events
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Page became visible - verify session integrity
      this.verifySessionIntegrity();
    }
  }

  /**
   * Verify session integrity
   */
  async verifySessionIntegrity() {
    try {
      const user = auth.currentUser;
      if (!user) {
        // Console statement removed for production
        this.logout();
        return;
      }

      // Check if token is still valid
      const token = await user.getIdTokenResult();
      const now = Date.now() / 1000;

      if (token.expirationTime < now) {
        // Console statement removed for production
        await this.refreshAuthToken();
      }

      // Verify device ID consistency
      const currentDeviceId = await this.getOrCreateDeviceId();
      if (currentDeviceId !== this.deviceId) {
        // Console statement removed for production
        this.logout();
      }
    } catch (error) {
      this.logout();
    }
  }

  /**
   * Monitor user activity and refresh session
   * Clear on inactivity (logout after 30 minutes idle)
   */
  monitorSessionActivity() {
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    const resetInactivityTimer = () => {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }

      this.inactivityTimer = setTimeout(() => {
        // Console statement removed for production
        this.logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Reset timer on user activity
    document.addEventListener('mousedown', resetInactivityTimer);
    document.addEventListener('keydown', resetInactivityTimer);
    document.addEventListener('scroll', resetInactivityTimer);

    // Initial timer
    resetInactivityTimer();
  }

  /**
   * Refresh authentication token
   * Called periodically (before expiration) to maintain session
   * 
   * This ensures token is always fresh and reduces window of compromise
   */
  async refreshAuthToken() {
    try {
      const user = auth.currentUser;
      if (user) {
        // Force token refresh
        const newToken = await user.getIdToken(true);
        this.lastRefreshTime = Date.now();
        // Console statement removed for production
      }
    } catch (error) {
      this.logout();
    }
  }

  /**
   * Get current auth token (for API calls)
   * Don't store this long-term - refresh as needed
   */
  async getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return token;
  }

  /**
   * Logout and cleanup
   */
  async logout() {
    try {
      // Signal logout to other tabs
      localStorage.setItem('logout', 'true');
      setTimeout(() => localStorage.removeItem('logout'), 1000);

      // Clear session storage
      sessionStorage.clear();

      // Clear sensitive data from IndexedDB
      try {
        await this.clearIndexedDB();
      } catch (error) {
        // Console statement removed for production
      }

      // Clear timers
      if (this.tokenRefreshTimer) clearInterval(this.tokenRefreshTimer);
      if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
      if (this.tabVisibilityTimer) clearInterval(this.tabVisibilityTimer);

      // Sign out from Firebase
      await signOut(auth);

      // Console statement removed for production
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Clear IndexedDB data (device-specific E2E keys)
   * Now also clears E2E encryption database with device-specific context
   */
  async clearIndexedDB() {
    // Clear the main session store
    await new Promise((resolve, reject) => {
      const request = indexedDB.open('CrashCabin_Secure', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['session'], 'readwrite');
        const store = transaction.objectStore('session');
        const clearRequest = store.clear();

        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      };
    });

    // Also clear device-specific E2E keys from e2eEncryption database
    // This ensures fresh keypair generation on next login to same device
    await new Promise((resolve) => {
      try {
        const request = indexedDB.open('e2eEncryption', 2);

        request.onerror = () => resolve(); // Silent fail, not critical

        request.onsuccess = () => {
          const db = request.result;
          
          if (db.objectStoreNames.contains('keys')) {
            try {
              const transaction = db.transaction(['keys'], 'readwrite');
              const store = transaction.objectStore('keys');
              
              // Get current device ID before clearing
              const deviceId = sessionStorage.getItem('deviceId');
              
              if (deviceId) {
                // Get user ID from auth to construct the device-specific key name
                // Since we might not have auth context here, we'll clear any device-specific keys we find
                // This is done by iterating through all keys (IndexedDB limitation - no prefix search)
                
                // For now, we just clear all keys - they'll be regenerated on next login
                // In production, you might want to be more selective
                const clearRequest = store.clear();
                
                clearRequest.onsuccess = () => {
                  db.close();
                  resolve();
                };
                clearRequest.onerror = () => {
                  db.close();
                  resolve();
                };
              } else {
                db.close();
                resolve();
              }
            } catch (error) {
              db.close();
              resolve();
            }
          } else {
            db.close();
            resolve();
          }
        };
      } catch (error) {
        resolve(); // Silent fail
      }
    });
  }

  /**
   * Clear encryption cache on device switch
   * 
   * Called when user switches devices to prevent stale encryption keys from interfering
   * More aggressive than clearIndexedDB - clears all encryption-related caches
   * 
   * Clears:
   * - IndexedDB e2eEncryption database (all old keys)
   * - IndexedDB CrashCabin_Secure database (session data)
   * - sessionStorage (device-specific data)
   * - localStorage (encryption cache entries)
   * 
   * @param {string} userId - Firebase user ID (for logging)
   * @returns {Promise<void>}
   */
  async clearEncryptionCacheOnDeviceSwitch(userId = null) {
    try {
      // 1. Clear main session database
      await new Promise((resolve) => {
        try {
          const request = indexedDB.open('CrashCabin_Secure', 1);

          request.onerror = () => resolve(); // Non-critical
          request.onsuccess = () => {
            const db = request.result;
            try {
              const transaction = db.transaction(['session'], 'readwrite');
              const store = transaction.objectStore('session');
              const clearRequest = store.clear();

              clearRequest.onsuccess = () => {
                db.close();
                resolve();
              };
              clearRequest.onerror = () => {
                db.close();
                resolve();
              };
            } catch (e) {
              db.close();
              resolve();
            }
          };
        } catch (e) {
          resolve();
        }
      });

      // 2. Clear E2E encryption keys database
      await new Promise((resolve) => {
        try {
          const request = indexedDB.open('e2eEncryption', 2);

          request.onerror = () => resolve(); // Non-critical
          request.onsuccess = () => {
            const db = request.result;
            try {
              if (db.objectStoreNames.contains('keys')) {
                const transaction = db.transaction(['keys'], 'readwrite');
                const store = transaction.objectStore('keys');
                const clearRequest = store.clear();

                clearRequest.onsuccess = () => {
                  db.close();
                  resolve();
                };
                clearRequest.onerror = () => {
                  db.close();
                  resolve();
                };
              } else {
                db.close();
                resolve();
              }
            } catch (e) {
              db.close();
              resolve();
            }
          };
        } catch (e) {
          resolve();
        }
      });

      // 3. Clear sessionStorage (device-specific data)
      sessionStorage.clear();

      // 4. Clear encryption-related localStorage entries (but keep user preferences)
      const keysToKeep = [
        'userPreferences',
        'theme',
        'language',
        'savedListings',
        'userProfile',
      ];

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && !keysToKeep.includes(key)) {
          // Remove encryption-related entries
          if (
            key.includes('encryption') ||
            key.includes('encryptionState') ||
            key.includes('device') ||
            key.includes('session') ||
            key.includes('Cache') ||
            key.includes('E2E')
          ) {
            localStorage.removeItem(key);
          }
        }
      }

      // 5. Record the cache clearance for audit
      if (userId) {
        // Store audit event (non-blocking)
        try {
          const timestamp = new Date().toISOString();
          localStorage.setItem(
            `cacheClear_${userId}_${timestamp}`,
            JSON.stringify({
              event: 'encryption_cache_cleared_on_device_switch',
              userId,
              timestamp,
            })
          );
          // Clean up old audit entries (keep last 5)
          const auditKeys = Object.keys(localStorage).filter(k => k.startsWith(`cacheClear_${userId}`));
          if (auditKeys.length > 5) {
            auditKeys.slice(0, -5).forEach(k => localStorage.removeItem(k));
          }
        } catch (e) {
          // Non-critical
        }
      }
    } catch (error) {
      console.error('Error clearing encryption cache on device switch:', error);
      // Non-critical, don't throw
    }
  }

  /**
   * Cleanup on page unload
   */
  cleanupSession() {
    if (this.tokenRefreshTimer) clearInterval(this.tokenRefreshTimer);
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
  }
}

// ============================================================================
// CSRF PROTECTION
// ============================================================================

/**
 * CSRF (Cross-Site Request Forgery) Prevention
 * 
 * Attack: Malicious site tricks you into making requests to banking site
 * Defense: Require CSRF token that attacker cannot access
 */

export class CSRFProtection {
  constructor() {
    this.token = this.generateCSRFToken();
  }

  /**
   * Generate CSRF token
   * Unique per session, changes on each successful action
   */
  generateCSRFToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem('csrfToken', token);
    return token;
  }

  /**
   * Get current CSRF token
   */
  getToken() {
    const token = sessionStorage.getItem('csrfToken');
    if (!token) {
      return this.generateCSRFToken();
    }
    return token;
  }

  /**
   * Verify CSRF token in request
   * Should be called on server side
   */
  verifyToken(token) {
    const storedToken = sessionStorage.getItem('csrfToken');
    return token === storedToken;
  }

  /**
   * Add CSRF token to fetch request headers
   */
  addToHeaders(headers = {}) {
    return {
      ...headers,
      'X-CSRF-Token': this.getToken()
    };
  }
}

// ============================================================================
// MULTI-DEVICE TRACKING
// ============================================================================

/**
 * Track devices accessing user's account
 * Store device information and detect suspicious access
 */

export class DeviceTracker {
  constructor() {
    this.devices = new Map();
  }

  /**
   * Register device
   * Called on each login
   */
  registerDevice(deviceInfo) {
    const deviceId = deviceInfo.deviceId;
    this.devices.set(deviceId, {
      ...deviceInfo,
      registeredAt: Date.now(),
      lastActive: Date.now()
    });
  }

  /**
   * Update device last active time
   */
  updateLastActive(deviceId) {
    const device = this.devices.get(deviceId);
    if (device) {
      device.lastActive = Date.now();
    }
  }

  /**
   * Get all active devices
   */
  getActiveDevices() {
    const now = Date.now();
    const inactiveThreshold = 30 * 24 * 60 * 60 * 1000; // 30 days
    
    return Array.from(this.devices.values())
      .filter(device => (now - device.lastActive) < inactiveThreshold)
      .sort((a, b) => b.lastActive - a.lastActive);
  }

  /**
   * Check if login is from new/unknown device
   */
  isUnknownDevice(deviceFingerprint) {
    for (const device of this.devices.values()) {
      if (device.fingerprint === deviceFingerprint) {
        return false;
      }
    }
    return true;
  }
}

// ============================================================================
// PASSWORD REQUIREMENTS
// ============================================================================

/**
 * Enforce strong password requirements
 * Prevents common weak passwords
 */

export const PasswordRequirements = {
  /**
   * Check if password meets security requirements
   * Requirements:
   * - Minimum 12 characters
   * - At least 1 uppercase letter
   * - At least 1 lowercase letter
   * - At least 1 number
   * - At least 1 special character
   * - Not in common password list
   */
  validate(password) {
    const issues = [];

    if (password.length < 12) {
      issues.push('Password must be at least 12 characters');
    }

    if (!/[A-Z]/.test(password)) {
      issues.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      issues.push('Password must contain at least one lowercase letter');
    }

    if (!/[0-9]/.test(password)) {
      issues.push('Password must contain at least one number');
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      issues.push('Password must contain at least one special character');
    }

    // Check against common passwords
    const commonPasswords = [
      'password', '123456', 'qwerty', 'abc123', 'password123',
      '111111', '1234567', '123123', '000000', 'admin'
    ];

    if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
      issues.push('Password contains common password patterns');
    }

    return {
      valid: issues.length === 0,
      issues
    };
  },

  /**
   * Get password strength score (0-100)
   */
  getStrength(password) {
    let score = 0;

    // Length score
    if (password.length >= 12) score += 20;
    if (password.length >= 16) score += 10;
    if (password.length >= 20) score += 10;

    // Character variety score
    if (/[a-z]/.test(password)) score += 15;
    if (/[A-Z]/.test(password)) score += 15;
    if (/[0-9]/.test(password)) score += 15;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 15;

    return Math.min(score, 100);
  }
};

// ============================================================================
// XSS PROTECTION
// ============================================================================

/**
 * Prevent XSS (Cross-Site Scripting) attacks
 * 
 * Attack: Inject malicious JavaScript into page
 * Defense: Sanitize all user input, don't use innerHTML with user data
 */

export const XSSProtection = {
  /**
   * Sanitize user input to prevent XSS
   * Removes all HTML tags and dangerous characters
   */
  sanitize(input) {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  },

  /**
   * Sanitize HTML while allowing safe tags
   * Used for rich text input
   */
  sanitizeHTML(html) {
    const div = document.createElement('div');
    // Use textContent to prevent script execution
    div.textContent = html;
    return div.innerHTML;
  },

  /**
   * Escape special HTML characters
   */
  escapeHTML(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
};

// ============================================================================
// INPUT VALIDATION
// ============================================================================

/**
 * Validate and sanitize all user input
 */

export const InputValidation = {
  /**
   * Validate email address
   */
  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  },

  /**
   * Validate username (alphanumeric, underscore, dash)
   */
  validateUsername(username) {
    const regex = /^[a-zA-Z0-9_-]{3,20}$/;
    return regex.test(username);
  },

  /**
   * Validate URL
   */
  validateURL(url) {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Sanitize message content
   * Remove harmful content while preserving text
   */
  sanitizeMessage(message) {
    // Remove any HTML tags
    return message
      .replace(/<[^>]*>/g, '')
      .trim()
      .slice(0, 5000); // Max length
  },

  /**
   * Validate message length
   */
  validateMessageLength(message) {
    return message.length > 0 && message.length <= 5000;
  }
};

export default {
  SecureSessionManager,
  CSRFProtection,
  DeviceTracker,
  PasswordRequirements,
  XSSProtection,
  InputValidation
};

